import React, { useMemo, useState } from "react";
import { Search, Heart, Info, SlidersHorizontal, ArrowUpDown, Share2, Landmark, CreditCard, TrendingUp, PiggyBank, ShieldCheck, Wallet, Zap } from "lucide-react";
import { inr } from "@/lib/api";
import { ShareCampaignDialog } from "@/components/ShareCampaignDialog";

const catIcon = (cat = "") => {
  const c = cat.toLowerCase();
  if (c.includes("loan")) return Landmark;
  if (c.includes("card")) return CreditCard;
  if (c.includes("demat") || c.includes("mutual") || c.includes("invest")) return TrendingUp;
  if (c.includes("saving") || c.includes("account")) return Wallet;
  if (c.includes("insurance")) return ShieldCheck;
  if (c.includes("cibil") || c.includes("credit")) return PiggyBank;
  return Zap;
};
const cleanName = (n = "") => n.replace(/\s*Campaign$/i, "").trim();
const absUrl = (s) => (!s ? "" : /^https?:\/\//i.test(s) ? s : `${window.location.origin}${s}`);

export function CampaignProductList({ campaigns, user, origin }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState("featured");
  const [favs, setFavs] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(`ck_favs_${user?.user_code}`) || "[]")); } catch (_) { return new Set(); }
  });
  const [shareCamp, setShareCamp] = useState(null);
  const [infoCamp, setInfoCamp] = useState(null);

  const cats = useMemo(() => {
    const s = new Set();
    campaigns.forEach((c) => c.category && s.add(c.category));
    return Array.from(s);
  }, [campaigns]);

  const toggleFav = (id) => {
    setFavs((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      try { localStorage.setItem(`ck_favs_${user?.user_code}`, JSON.stringify([...n])); } catch (_) {}
      return n;
    });
  };

  const maxPayout = (c) => {
    const slabMax = Math.max(0, ...(c.payout_slabs || []).map((s) => Number(s.payout) || 0));
    return Math.max(slabMax, Number(c.payout_pool) || 0);
  };

  const shown = useMemo(() => {
    let list = campaigns.filter((c) => c.campaign_code);
    if (cat !== "all") list = list.filter((c) => (c.category || "Other") === cat);
    const query = q.trim().toLowerCase();
    if (query) list = list.filter((c) => [c.name, c.description, c.category].filter(Boolean).join(" ").toLowerCase().includes(query));
    if (sort === "payout_desc") list = [...list].sort((a, b) => maxPayout(b) - maxPayout(a));
    else if (sort === "name") list = [...list].sort((a, b) => cleanName(a.name).localeCompare(cleanName(b.name)));
    else list = [...list].sort((a, b) => (b.top_converting === true) - (a.top_converting === true) || (b.featured === true) - (a.featured === true));
    // favourites first
    return [...list].sort((a, b) => (favs.has(b.id) ? 1 : 0) - (favs.has(a.id) ? 1 : 0));
    // eslint-disable-next-line
  }, [campaigns, cat, q, sort, favs]);

  return (
    <div className="space-y-4">
      {/* search + filter/sort bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-3 shadow-sm">
        <div className="relative">
          <Search className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ width: 18, height: 18 }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Products / Campaign" data-testid="plist-search"
            className="w-full h-11 pl-10 pr-3 rounded-xl bg-slate-50 border border-slate-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-violet-500/40" />
        </div>
        <div className="flex items-center gap-2 mt-3">
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-1" data-testid="plist-cat-chips">
            {["all", ...cats].map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`plist-cat-${String(c).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${cat === c ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-400"}`}>
                {c === "all" ? "All" : c}
              </button>
            ))}
          </div>
          <div className="relative shrink-0">
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="plist-sort"
              className="h-9 appearance-none rounded-xl border border-slate-200 bg-white pl-8 pr-7 text-xs font-bold text-slate-600 cursor-pointer">
              <option value="featured">Top</option>
              <option value="payout_desc">Payout ↓</option>
              <option value="name">A–Z</option>
            </select>
            <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* product cards */}
      <div className="space-y-4" data-testid="plist-cards">
        {shown.map((c) => {
          const Icon = catIcon(c.category);
          const img = c.desktop_creative || c.mobile_creative || "";
          const pct = c.payout_type === "percent" && c.payout_percent;
          const fav = favs.has(c.id);
          const url = `${origin}/apply/${c.campaign_code}?user_id=${user?.user_code}`;
          return (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={`plist-card-${c.campaign_code}`}>
              {/* top row: earn pill + info + top selling */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-baseline gap-1 bg-violet-100 rounded-lg px-2.5 py-1">
                  <span className="text-[13px] font-extrabold text-[#0B223D]">Earn Upto</span>
                  <span className="text-[15px] font-extrabold text-rose-600 font-mono-num">{pct ? `${c.payout_percent}%` : inr(maxPayout(c))}</span>
                </span>
                <button onClick={() => setInfoCamp(infoCamp?.id === c.id ? null : c)} data-testid={`plist-info-${c.campaign_code}`}
                  className="w-6 h-6 rounded-full border border-violet-300 text-violet-600 flex items-center justify-center hover:bg-violet-50" title="Payout details"><Info className="w-3.5 h-3.5" /></button>
                {c.top_converting && (
                  <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-extrabold text-teal-600 tracking-wide">
                    <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" /> TOP SELLING
                  </span>
                )}
              </div>
              {infoCamp?.id === c.id && (
                <div className="mt-2 bg-violet-50 border border-violet-100 rounded-lg p-2.5 text-xs text-slate-600" data-testid={`plist-info-panel-${c.campaign_code}`}>
                  {pct
                    ? <>Payout: <b className="font-mono-num">{c.payout_percent}%</b> of disbursed/converted amount.</>
                    : <>Base payout: <b className="font-mono-num">{inr(c.payout_pool)}</b> per confirmed conversion.</>}
                  {(c.payout_slabs || []).length > 0 && [...c.payout_slabs].sort((a, b) => a.min_conversions - b.min_conversions).map((s, i) => (
                    <p key={i} className="mt-0.5">{s.min_conversions}+ leads → <b className="font-mono-num text-violet-700">{inr(s.payout)}</b> / conversion</p>
                  ))}
                </div>
              )}

              {/* logo + name + desc */}
              <div className="flex items-center gap-3.5 mt-3.5">
                <div className="w-[76px] h-[76px] shrink-0 rounded-2xl border border-slate-200 bg-white flex items-center justify-center overflow-hidden p-1.5">
                  {img
                    ? <img src={absUrl(img)} alt={cleanName(c.name)} loading="lazy" className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    : <Icon className="w-8 h-8 text-violet-500" />}
                </div>
                <div className="min-w-0">
                  <h3 className="font-display font-extrabold text-[17px] text-[#0B223D] leading-snug">{cleanName(c.name)}</h3>
                  {c.description && <p className="text-[13px] text-slate-500 mt-0.5 line-clamp-2">{c.description}</p>}
                </div>
              </div>

              {/* favourite + share */}
              <div className="flex items-center justify-between gap-3 mt-4">
                <button onClick={() => toggleFav(c.id)} data-testid={`plist-fav-${c.campaign_code}`} className="flex flex-col items-center gap-0.5 text-slate-400 hover:text-rose-500 transition-colors">
                  <Heart className={`w-6 h-6 ${fav ? "fill-rose-500 text-rose-500" : ""}`} />
                  <span className="text-[10px] font-semibold">Favourite</span>
                </button>
                <button onClick={() => setShareCamp({ c, url })} data-testid={`plist-share-${c.campaign_code}`}
                  className="flex-1 max-w-[280px] h-12 rounded-full bg-violet-600 hover:bg-violet-700 active:scale-[0.98] text-white font-bold text-[15px] flex items-center justify-center gap-2 shadow-md shadow-violet-600/25 transition-all">
                  Share to Customer
                  <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><Share2 className="w-4 h-4" /></span>
                </button>
              </div>
            </div>
          );
        })}
        {shown.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-400 text-sm" data-testid="plist-empty">
            {q || cat !== "all" ? "Is filter/search me koi campaign nahi mila." : "Abhi koi live campaign nahi hai."}
          </div>
        )}
      </div>

      {shareCamp && <ShareCampaignDialog campaign={shareCamp.c} url={shareCamp.url} user={user} onClose={() => setShareCamp(null)} />}
    </div>
  );
}
